import Vue from "vue";
import Vuex from "vuex";
import auth from "../services/Auth";
import AWS from "aws-sdk/global";
import screenfull from "screenfull";
import router from "@/router";
import Talk from "talkjs";
import axios from "axios";
import { pageOptionObject, ActiveNavigation } from "@/types/interfaces.ts";

/**
 * Modules
 * please create a module for every API endpoint introduced.
 * modules are in ./vuex-modules/
 */

Vue.use(Vuex);

export default new Vuex.Store({
    state: {
        isFullScreen: false,
        enableHeader: true,
        enableFooter: true,
        themeConfigLoaded: false,
        themeConfig: {
            sessionOptions: {},
            speakeasy: {},
            ads: "",
            favicon: "",
            logo: "",
            logoGate: "",
            slogan: "",
            logoMeetingView: "",
            footerLogo: "",
            hostLogoGate: "",
            mobileLogo: "",
            bgImage: "",
            navImage: "",
            decorImage: "",
            logoName: "",
            logoDescription: "",
            splashImage: "",
            conferenceName: "",
            conferenceYear: "",
            conferenceId: "",
            displayNameInHeader: true,
            layoutOptions: "",
            layoutImages: "",
            activeNavigation: "",
            fontConfig: "",
            homeLabel: "",
            sessionLabel: "",
            attendeeLabels: "",
            tradeshowLabel: "",
            schedulePageLabel: "",
            messagePageLabel: "",
            usesLocalDataSearch: "",
            socialMediaAccount: "",
            fontFamily: "",
            webfontSrc: "",
            cssVariables: "",
            customLayout: "",
            timeZone: "",
            pageOptions: [],
            primaryInterests: [],
            forceAuth: false,
            apiBaseURL: "",
            apiBaseURLDev: "",
            authConfig: {
                loginURL: "",
                logoutURL: "",
                tokenURL: "",
                grantType: "",
                clientId: ""
            },
            awsConfig: {},
            helpdeskConnection: {
                region: "",
                api: "",
                instanceId: "",
                contactFlowId: ""
            },
            sponsorVideos: [],
            talkSettings: {
                appId: "",
                useSignature: false,
                meetingPrefix: ""
            }
        },
        user: {
            id: "",
            username: "",
            name: "",
            companyName: "",
            companyId: ""
        },
        userCredentials: {
            idToken: "",
            accessToken: "",
            refreshToken: "",
            expires: 0,
            isRefreshing: false
        },
        exhibitorSearchResults: [],
        attendeeSearchResults: [],
        talkData: {
            user: null,
            session: null,
            unreads: 0
        }
    },
    getters: {
        speakeasyOptions: state => {
            return state.themeConfig.speakeasy;
        },

        ads: state => {
            return state.themeConfig.ads;
        },

        helpdeskInstanceRegion: state => {
            return state.themeConfig.helpdeskConnection.region;
        },

        helpdeskInstanceApi: state => {
            return state.themeConfig.helpdeskConnection.api;
        },

        helpdeskInstanceId: state => {
            return state.themeConfig.helpdeskConnection.instanceId;
        },

        helpdeskContactFlowId: state => {
            return state.themeConfig.helpdeskConnection.contactFlowId;
        },

        isChatEnabled: state => {
            const connectionData = state.themeConfig.helpdeskConnection;

            for (const [key, value] of Object.entries(connectionData)) {
                if (!value) {
                    return false;
                }
            }

            return true;
        },

        timeZone: state => {
            return state.themeConfig.timeZone;
        },

        usesLocalDataSearch: state => {
            return state.themeConfig.usesLocalDataSearch;
        },

        layoutImages: state => {
            return state.themeConfig.layoutImages;
        },

        hostLogoGate: state => {
            return state.themeConfig.hostLogoGate;
        },
        socialMediaAccount: state => {
            return state.themeConfig.socialMediaAccount;
        },

        layoutOptions: state => {
            return state.themeConfig.layoutOptions;
        },

        sponsorVideos: state => {
            return state.themeConfig.sponsorVideos;
        },

        homeLabel: state => {
            return state.themeConfig.homeLabel;
        },

        sessionLabel: state => {
            return state.themeConfig.sessionLabel;
        },

        tradeshowLabel: state => {
            return state.themeConfig.tradeshowLabel;
        },

        attendeeLabels: state => {
            return state.themeConfig.attendeeLabels;
        },

        messagePageLabel: state => {
            return state.themeConfig.messagePageLabel;
        },

        schedulePageLabel: state => {
            return state.themeConfig.schedulePageLabel;
        },

        fontConfig: state => {
            return state.themeConfig.fontConfig;
        },

        cssVariables: state => {
            return state.themeConfig.cssVariables;
        },

        getPageOptions: state => (page: string) => {
            const result = state.themeConfig.pageOptions.find(
                (item: pageOptionObject) => page === item.page
            );
            return result;
        },
        getPrimaryInterests: state => (interest: string) => {
            return state.themeConfig.primaryInterests.find(
                (item: any) => item.name === interest
            );

            // interestPath = `${process.env.BASE_URL}interest/${specificInterest}`;
        },

        activeNavigation: state => {
            return state.themeConfig.activeNavigation;
        },
        getNavItem: (state, getters) => (page: string) => {
            return getters.activeNavigation.find(
                (item: ActiveNavigation) => page === item.name
            );
        },
        displayNameInHeader: state => {
            return state.themeConfig.displayNameInHeader;
        },
        slogan: state => {
            return state.themeConfig.slogan;
        },
        logo: state => {
            let returnValue = "";
            if (state.themeConfig.logo) {
                returnValue = `${process.env.BASE_URL}logos/${state.themeConfig.logo}`;
            }
            return returnValue;
        },
        logoMeetingView: state => {
            let returnValue = "";
            if (state.themeConfig.logoMeetingView) {
                returnValue = `${process.env.BASE_URL}logos/${state.themeConfig.logoMeetingView}`;
            }
            return returnValue;
        },
        getBackgroundImage: state => {
            return state.themeConfig.bgImage;
        },
        logoGate: state => {
            return state.themeConfig.logoGate;
        },
        navImage: state => {
            return state.themeConfig.navImage;
        },
        favicon: state => {
            return state.themeConfig.favicon;
        },
        footerLogo: state => {
            return state.themeConfig.footerLogo;
        },
        mobileLogo: state => {
            return state.themeConfig.mobileLogo;
        },
        decorImage: state => {
            return state.themeConfig.decorImage;
        },
        logoName: state => {
            return state.themeConfig.logoName;
        },
        logoDesc: state => {
            return state.themeConfig.logoDescription;
        },
        splashImage: state => {
            return state.themeConfig.splashImage;
        },
        conferenceName: state => {
            return state.themeConfig.conferenceName;
        },
        conferenceYear: state => {
            return state.themeConfig.conferenceYear;
        },
        talkAppId: state => state.themeConfig.talkSettings.appId,
        talkUseSignature: state => state.themeConfig.talkSettings.useSignature,
        talkMeetingPrefix: state =>
            state.themeConfig.talkSettings.meetingPrefix,
        apiBaseURL: state => {
            console.log("enviornment:", process.env.NODE_ENV);
            if (
                process.env.NODE_ENV === "production" ||
                !state.themeConfig.apiBaseURLDev
            ) {
                return state.themeConfig.apiBaseURL;
            }
            return state.themeConfig.apiBaseURLDev;
        },
        authConfig: state => state.themeConfig.authConfig,
        awsConfig: state => state.themeConfig.awsConfig,
        user: state => state.user,
        isRegistered: state =>
            auth.isRegistered(
                state.userCredentials.idToken,
                state.themeConfig.conferenceId
            ),
        isAuthenticated: state => !!state.userCredentials.idToken,
        isAuthorized: (state, getters) =>
            getters.isAuthenticated && getters.isRegistered,
        isSessionExpired: state => {
            const currentTimeSeconds = new Date().getTime() / 1000;
            return currentTimeSeconds >= state.userCredentials.expires;
        },
        exhibitorSearchResults: state => state.exhibitorSearchResults,
        attendeeSearchResults: state => state.attendeeSearchResults,
        talkUser: state => state.talkData.user,
        talkSession: state => state.talkData.session,
        talkUnreads: state => state.talkData.unreads,
        idToken: state => state.userCredentials.idToken,
        webfontSrc: state => state.themeConfig.webfontSrc
    },

    mutations: {
        setIsFullScreen(state) {
            if (screenfull.isEnabled) {
                state.isFullScreen = screenfull.isFullscreen;
            } else {
                state.isFullScreen = false;
            }
        },
        setUser(state, payload = {}) {
            if (payload.idToken) {
                const idTokenData = auth.parseJWT(payload.idToken);
                // console.log(idTokenData, "idTokenData");
                state.user.id =
                    idTokenData["https://bespeake.io/attendeeId"] ||
                    idTokenData.sub;
                state.user.username = idTokenData.username || idTokenData.email;
                state.user.name = idTokenData.name;
                state.user.companyName =
                    idTokenData["https://bespeake.io/companyName"];
                state.user.companyId =
                    idTokenData["https://bespeake.io/companyId"];
                state.userCredentials.idToken = payload.idToken;
                state.userCredentials.accessToken = payload.accessToken;
                state.userCredentials.refreshToken = payload.refreshToken;
                state.userCredentials.expires = idTokenData.exp;
            } else {
                state.user.id = "";
                state.user.username = "";
                state.user.name = "";
                state.user.companyName = "";
                state.user.companyId = "";
                state.userCredentials.idToken = "";
                state.userCredentials.accessToken = "";
                state.userCredentials.refreshToken = "";
                state.userCredentials.expires = 0;
            }
            localStorage.setItem("token", JSON.stringify(payload));
        },
        setRefreshing(state, payload) {
            state.userCredentials.isRefreshing = payload;
            // console.log(`set refreshing to ${payload}`);
        },
        setThemeConfig(state, payload) {
            state.themeConfig = payload;
        },
        setThemeConfigLoaded(state, payload) {
            state.themeConfigLoaded = payload;
        },
        setEnableHeader(state, payload) {
            state.enableHeader = payload;
        },
        setEnableFooter(state, payload) {
            state.enableFooter = payload;
        },
        setCSSvariables(state) {
            if (
                state.themeConfigLoaded &&
                state.themeConfig &&
                state.themeConfig.cssVariables
            ) {
                if (!state.themeConfig.cssVariables) return;

                Object.entries(state.themeConfig.cssVariables).forEach(
                    ([key, value]) => {
                        const documentElement = document.documentElement;

                        if (0 === key.indexOf("--")) {
                            documentElement.style.setProperty(key, value);
                        }
                    }
                );
            }
        },
        exhibitorSearchResults(state, payload) {
            state.exhibitorSearchResults = payload;
        },
        attendeeSearchResults(state, payload) {
            state.attendeeSearchResults = payload;
        },
        setTalkUser(state, payload) {
            state.talkData.user = payload;
        },
        setTalkSession(state, payload) {
            state.talkData.session = payload;
        },
        setTalkUnreads(state, payload) {
            state.talkData.unreads = payload;
        }
    },
    actions: {
        toggleFullScreen: ({ commit }) => {
            Vue.prototype.MgToggleFullScreen().finally(() => {
                commit("setIsFullScreen");
            });
        },
        enableWrapper: ({ commit }) => {
            commit("setEnableHeader", true);
            commit("setEnableFooter", true);
        },
        disableWrapper: ({ commit }) => {
            commit("setEnableHeader", false);
            commit("setEnableFooter", false);
        },
        loginUser: async ({ commit, state, dispatch }, token) => {
            if (!token.idToken) return Promise.reject("no id token");
            try {
                const awsCredentials = await auth.getAWSIdentity(token.idToken);
                AWS.config.credentials = awsCredentials;
                AWS.config.update({ region: "us-east-1" });
            } catch (err) {
                return Promise.reject("Did not receive backend credentials.");
            }

            commit("setUser", token);

            dispatch("setupTalk");

            // establish Rollbar user
            Vue.prototype.$rollbar.configure({
                payload: {
                    person: {
                        id: state.user.id,
                        username: state.user.username
                    }
                }
            });
            const currentTimeSeconds = new Date().getTime() / 1000;
            // refresh 5 minutes before expiration
            const tokenRefreshTimer =
                state.userCredentials.expires - currentTimeSeconds - 300;
            try {
                // console.log("set timeout seconds");
                // console.log(tokenRefreshTimer);
                setTimeout(
                    async () =>
                        await dispatch("refreshToken").catch(() => {
                            commit("setUser", {});
                            router.push("/gate");
                        }),
                    tokenRefreshTimer * 1000
                );
                // console.log("done done");

                const selectedEntrance = localStorage.getItem(
                    "selectedEntrance"
                );
                if (selectedEntrance) {
                    router.push("/entrance");
                } else {
                    const storedPath = localStorage.getItem("storedPath");
                    if (storedPath) {
                        localStorage.removeItem("storedPath");
                        router.push(storedPath);
                    }
                }
            } catch (err) {
                console.error(err);
                console.log("rejecting");
                return Promise.reject("Failed token refresh");
            }
        },
        refreshToken: async ({ state, getters, dispatch, commit }) => {
            console.log("refreshing");
            if (state.userCredentials.isRefreshing)
                return Promise.reject("Already refreshing");
            commit("setRefreshing", true);
            try {
                if (!state.userCredentials.refreshToken) {
                    return Promise.reject("No refresh token");
                }
                const token = await auth
                    .refreshToken(
                        getters.authConfig,
                        state.userCredentials.refreshToken
                    )
                    .catch(err => {
                        console.log(err);
                        Vue.prototype.$rollbar.warning(
                            `Failed to refresh token ${state.userCredentials.refreshToken}: ${err.message}`
                        );
                        return Promise.reject("Failed to refresh token");
                    });
                await dispatch("loginUser", token);
            } catch (err) {
                console.log("refresh has failed");
                return Promise.reject("failed refresh");
            } finally {
                commit("setRefreshing", false);
            }
        },
        logout: async ({ commit, state }) => {
            commit("setUser", {});
            if (state.themeConfig.authConfig.logoutURL) {
                window.location.href = state.themeConfig.authConfig.logoutURL;
            } else {
                router.push("/gate");
            }
        },
        setupTalk: ({ commit, state, getters }) => {
            /** TODO: Change this into using an API service. Currently it's duplicating because importing the API service doesn't
                work properly here. */
            if (!getters.talkUser && getters.talkAppId) {
                Talk.ready.then(() => {
                    const talkId = getters.talkMeetingPrefix
                        ? `${getters.talkMeetingPrefix}-${state.user.id}`
                        : state.user.id;
                    const talkUser = new Talk.User({
                        id: talkId,
                        name: state.user.name,
                        role: "attendee",
                        custom: {
                            companyName: state.user.companyName
                                ? state.user.companyName
                                : ""
                        }
                    });
                    commit("setTalkUser", talkUser);

                    if (getters.talkUseSignature) {
                        const baseURL = getters.apiBaseURL;
                        const apiClient = axios.create({
                            baseURL,
                            withCredentials: false,
                            headers: {
                                Accept: "application/json",
                                "Content-Type": "application/json"
                            }
                        });

                        apiClient
                            .get("/talk-signature", {
                                headers: {
                                    Authorization: `bearer ${getters.idToken}`
                                }
                            })
                            .then(response => {
                                if (response && response.status === 200) {
                                    const talkSession = new Talk.Session({
                                        appId: getters.talkAppId,
                                        me: talkUser,
                                        signature: response.data.talkSignature
                                    });

                                    talkSession.unreads.on(
                                        "change",
                                        conversationIds => {
                                            const amount =
                                                conversationIds.length;
                                            commit("setTalkUnreads", amount);
                                        }
                                    );
                                    commit("setTalkSession", talkSession);
                                }
                            });
                    } else {
                        const talkSession = new Talk.Session({
                            appId: getters.talkAppId,
                            me: talkUser
                        });

                        talkSession.unreads.on("change", conversationIds => {
                            const amount = conversationIds.length;
                            commit("setTalkUnreads", amount);
                        });
                        commit("setTalkSession", talkSession);
                    }
                });
            }
        }
    }
});
